import type {
    PresenceChangeCountConstantType,
    PresenceExitStyleConstantType,
    PresenceEntriesConstantType,
    PresenceSameConstantType,
    PresenceEntry
} from "./type";
import type {
    MotionStyleType
} from "../../helpers/motion";

export const PRESENCE_COLLAPSE_START = 0.6;

export const PRESENCE_LIST_CHANGE_LIMIT = 3;

export const getPresenceEntries = ({
    previous,
    current
}: PresenceEntriesConstantType): Array<PresenceEntry> => {
    const currentKeys = new Set(current.map((child) => String(child.key)));

    const entries: Array<PresenceEntry> = [];

    let cursor = 0;

    const pushExiting = (end: number) => {
        previous.slice(cursor, end).forEach((entry) => {
            if(!currentKeys.has(entry.key)) {
                entries.push({
                    isPresent: false,
                    key: entry.key
                });
            }
        });
    };

    current.forEach((child) => {
        const key = String(child.key);

        const index = previous.findIndex((entry) => entry.key === key);

        if(index >= cursor) {
            pushExiting(index);

            cursor = index + 1;
        }

        entries.push({
            isPresent: true,
            key
        });
    });

    pushExiting(previous.length);

    return entries;
};

export const getPresenceIsSame = ({
    previous,
    next
}: PresenceSameConstantType): boolean => {
    return previous.length === next.length && previous.every((entry, index) => entry.key === next[index]?.key && entry.isPresent === next[index]?.isPresent);
};

export const getPresenceChangeCount = ({
    previous,
    next
}: PresenceChangeCountConstantType): number => {
    const previousPresent = new Set(previous.filter((entry) => entry.isPresent).map((entry) => entry.key));

    const nextPresent = new Set(next.filter((entry) => entry.isPresent).map((entry) => entry.key));

    const added = [...nextPresent].filter((key) => !previousPresent.has(key)).length;

    const removed = [...previousPresent].filter((key) => !nextPresent.has(key)).length;

    return added + removed;
};

export const getPresenceExitStyle = ({
    isReduceMotionEnabled,
    progress,
    axis,
    size,
    gap
}: PresenceExitStyleConstantType): MotionStyleType => {
    if(isReduceMotionEnabled) {
        return {
            opacity: progress
        };
    }

    const collapse = progress.interpolate({
        inputRange: [
            0,
            PRESENCE_COLLAPSE_START
        ],
        extrapolate: "clamp",
        outputRange: [
            0,
            1
        ]
    });

    const getRange = (from: number, to: number) => collapse.interpolate({
        outputRange: [
            from,
            to
        ],
        inputRange: [
            0,
            1
        ]
    });

    if(axis === "x") {
        return {
            width: getRange(0, size.width),
            marginEnd: getRange(-gap, 0),
            overflow: "hidden",
            opacity: progress
        };
    }

    return {
        height: getRange(0, size.height),
        marginBottom: getRange(-gap, 0),
        overflow: "hidden",
        opacity: progress
    };
};
